/**
 * dashboards/hooks/useDashboards.js
 *
 * Listing + per-dashboard fetch + per-widget data + mutations. The
 * useWidgetData hook is the single entry point widgets use to retrieve
 * their chart data; today it falls back to client-side aggregation,
 * tomorrow it joins the batched /dashboards/:id/run cache.
 */
import {
  useQuery, useMutation, invalidateQueries,
  getQueryData, setQueryData,
} from '../../analytics-shared/hooks/queryShim';
import { dashboardsApi } from '../api/dashboardsApi';
import { analyticsClient, unwrap } from '../../analytics-shared/api/client';

const LIST_KEY = ['dashboards', 'list'];

export function useDashboardsList() {
  const q = useQuery({
    queryKey: ['dashboards', 'list'],
    queryFn: () => dashboardsApi.list(),
    // Lowered from 15s — pairs with the queryShim invalidate-refetch
    // fix so favorite/folder/visibility mutations show in the list
    // immediately rather than after a page reload.
    staleTime: 5_000,
  });
  return { ...q, dashboards: q.data || [] };
}

export function useDashboard(id) {
  return useQuery({
    queryKey: ['dashboards', 'detail', id],
    queryFn: () => dashboardsApi.get(id),
    enabled: !!id,
    staleTime: 5_000,
  });
}

export function useDashboardRun(id /* , globalFilters */) {
  return useQuery({
    queryKey: ['dashboards', 'run', id],
    queryFn: () => dashboardsApi.run(id),
    enabled: !!id,
    // Tighter than the old 10s so re-entries pick up CRM mutations
    // without requiring the user to hit Refresh.
    staleTime: 2_000,
  });
}

/**
 * Per-widget data fetch.
 *
 * After wedge 5 this is a one-shot POST to the server-side runner: the
 * backend's analytics_engine handles record fetch + filtering + chart
 * aggregation. The hook still keys on the widget's config knobs so the
 * cache invalidates correctly when the user retoggles axes/aggregate
 * inside the WidgetSettingsPanel.
 *
 * The shape returned mirrors what the JS engine used to build:
 *   - table widgets:  { report, fieldMap, records, tableColumns }
 *   - chart widgets:  { report, fieldMap, records, data, chartDef }
 *   - misconfigured:  { ..., unconfigured: true, data: [] }
 *   - errors:         { error: "...", report? }
 *
 * Render code in DashboardWidget.jsx is unchanged.
 */
export function useWidgetData(widget) {
  const cfg = widget?.config || {};
  return useQuery({
    queryKey: [
      'widget',
      widget?.reportId,
      widget?.chartType,
      cfg.xField,
      cfg.yField,
      cfg.aggregate,
      Array.isArray(cfg.tableColumns) ? cfg.tableColumns.join(',') : '',
    ],
    enabled: !!widget?.reportId,
    // Lowered: dashboards need to reflect CRM mutations promptly on
    // re-entry. The key already includes the widget config knobs, so
    // axis changes still refetch immediately regardless of this.
    staleTime: 5_000,
    queryFn: async () => {
      // Single backend round-trip — server does the heavy lifting
      // (records fetch + filters + joins + chart aggregation). No
      // remnant of the JS engine in this path.
      try {
        return await analyticsClient.post('/widgets/run', {
          reportId: widget.reportId,
          chartType: widget.chartType,
          xField: cfg.xField,
          yField: cfg.yField,
          aggregate: cfg.aggregate || 'COUNT',
          tableColumns: Array.isArray(cfg.tableColumns) ? cfg.tableColumns : [],
        }).then(unwrap);
      } catch (err) {
        if (err?.status === 404) return { error: 'Report not found' };
        return { error: err?.message || 'Failed to load widget' };
      }
    },
  });
}

export function useDashboardMutations() {
  const save = useMutation({
    mutationFn: (dashboard) => dashboardsApi.save(dashboard),
    onSuccess: () => invalidateQueries(['dashboards']),
  });
  // Optimistic delete — yank the card the moment the click registers
  // so the user doesn't see a frozen UI while the network resolves.
  const remove = useMutation({
    mutationFn: (id) => dashboardsApi.remove(id),
    onMutate: (id) => {
      const prev = getQueryData(LIST_KEY);
      if (Array.isArray(prev)) {
        setQueryData(LIST_KEY, prev.filter((d) => d.id !== id));
      }
      return { prev };
    },
    onSuccess: () => invalidateQueries(['dashboards']),
  });
  const duplicate = useMutation({
    mutationFn: (id) => dashboardsApi.duplicate(id),
    onSuccess: () => invalidateQueries(['dashboards']),
  });
  // Optimistic favorite flip — same idea as delete.
  const toggleFavorite = useMutation({
    mutationFn: (id) => dashboardsApi.toggleFavorite(id),
    onMutate: (id) => {
      const prev = getQueryData(LIST_KEY);
      if (Array.isArray(prev)) {
        setQueryData(LIST_KEY, prev.map((d) => (
          d.id === id ? { ...d, favorite: !d.favorite } : d
        )));
      }
      return { prev };
    },
    onSuccess: () => invalidateQueries(['dashboards']),
  });
  const markViewed = useMutation({
    mutationFn: (id) => dashboardsApi.markViewed(id),
    onSuccess: () => invalidateQueries(['dashboards']),
  });
  const moveToFolder = useMutation({
    mutationFn: ({ id, folder }) => dashboardsApi.moveToFolder(id, folder),
    onSuccess: () => invalidateQueries(['dashboards']),
  });
  const saveLayout = useMutation({
    mutationFn: ({ id, layouts, version }) => dashboardsApi.saveLayout(id, layouts, version),
    onSuccess: () => invalidateQueries(['dashboards']),
  });
  return { save, remove, duplicate, toggleFavorite, markViewed, moveToFolder, saveLayout };
}
